import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  Avatar,
  Tooltip,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Logout, 
  Settings, 
  Newspaper, 
  Campaign, 
  Headphones,
  DarkMode,
  LightMode,
  MusicNote,
  Podcasts,
  CleaningServices,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { logout } from '../../features/auth/slices/authSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import { useThemeMode } from '../../ThemeModeContext';
import apiClient from '../../api/apiClient';

const AppHeader: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation(); // Get current location to determine active page
  const { user } = useSelector((state: RootState) => state.auth);
  const { mode, toggleMode } = useThemeMode();
  
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const [clearCacheBusy, setClearCacheBusy] = useState(false);
  const [clearCacheResultOpen, setClearCacheResultOpen] = useState(false);
  const [clearCacheResult, setClearCacheResult] = useState<{
    success?: boolean;
    message?: string;
    errors?: any;
    raw?: any;
  } | null>(null);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleCloseUserMenu();
  };

  const handleClearCache = async () => {
    handleCloseUserMenu();
    setClearCacheBusy(true);
    setClearCacheResult(null);

    try {
      // NOTE: apiClient baseURL is already configured; include /api prefix because the server route is /api/publisher/cache/clear
      const response = await apiClient.post('/publisher/cache/clear');
      const data = response?.data;

      const success =
        typeof data?.success === 'boolean'
          ? data.success
          : true;

      const message =
        data?.message ||
        (success ? 'Cache cleared successfully.' : 'Cache clear finished with errors.');

      setClearCacheResult({
        success,
        message,
        errors: data?.errors,
        raw: data,
      });
    } catch (err: any) {
      const apiError = err?.response?.data;
      const message =
        apiError?.error ||
        apiError?.message ||
        err?.message ||
        'Failed to clear cache.';

      setClearCacheResult({
        success: false,
        message,
        errors: apiError,
        raw: apiError,
      });
    } finally {
      setClearCacheBusy(false);
      setClearCacheResultOpen(true);
    }
  };

  const pages = [
    { title: 'Publishers', path: '/publishers', icon: <Newspaper fontSize="small" sx={{ mr: 1 }} /> },
    { title: 'Advertisers', path: '/advertisers', icon: <Campaign fontSize="small" sx={{ mr: 1 }} /> },
    { title: 'Podcasts', path: '/podcasts', icon: <Podcasts fontSize="small" sx={{ mr: 1 }} /> },
    { title: 'Musicians', path: '/musicians', icon: <MusicNote fontSize="small" sx={{ mr: 1 }} /> },
    { title: 'Listener Preview', path: '/listener-experience', icon: <Headphones fontSize="small" sx={{ mr: 1 }} /> },
  ];

  // Check if a page is active
  const isActivePage = (path: string) => {
    return location.pathname.startsWith(path);
  };

  // Build/run metadata injected at build-time by the pipeline
  const buildInfo = {
    runNumber: process.env.REACT_APP_BUILD_RUN_NUMBER,
    runId: process.env.REACT_APP_BUILD_RUN_ID,
    runAttempt: process.env.REACT_APP_BUILD_RUN_ATTEMPT,
    workflow: process.env.REACT_APP_BUILD_WORKFLOW,
    ref: process.env.REACT_APP_BUILD_REF,
    branch: process.env.REACT_APP_BUILD_BRANCH,
    sha: process.env.REACT_APP_BUILD_SHA,
    actor: process.env.REACT_APP_BUILD_ACTOR,
    date: process.env.REACT_APP_BUILD_DATE,
    message: process.env.REACT_APP_BUILD_COMMIT_MESSAGE,
    runUrl: process.env.REACT_APP_BUILD_RUN_URL,
    commitUrl: process.env.REACT_APP_BUILD_COMMIT_URL,
    environment: process.env.REACT_APP_BUILD_ENVIRONMENT,
    version: process.env.REACT_APP_BUILD_VERSION,
  };

  const formattedDate = (() => {
    const d = buildInfo.date ? new Date(buildInfo.date) : undefined;
    return d && !isNaN(d.getTime()) ? d.toLocaleString() : undefined;
  })();

  const VersionOverlay = () => {
    if (!buildInfo.runNumber) return null;
    return (
      <Tooltip
        arrow
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>Build #{buildInfo.runNumber}{buildInfo.version ? ` • v${buildInfo.version}` : ''}</Typography>
            {buildInfo.workflow && (
              <Typography variant="caption" sx={{ display: 'block' }}>Workflow: {buildInfo.workflow}</Typography>
            )}
            {formattedDate && (
              <Typography variant="caption" sx={{ display: 'block' }}>Date: {formattedDate}</Typography>
            )}
            {buildInfo.branch && (
              <Typography variant="caption" sx={{ display: 'block' }}>Branch: {buildInfo.branch}</Typography>
            )}
            {buildInfo.sha && (
              <Typography variant="caption" sx={{ display: 'block' }}>SHA: {buildInfo.sha.substring(0, 7)}</Typography>
            )}
            {buildInfo.environment && (
              <Typography variant="caption" sx={{ display: 'block' }}>Environment: {buildInfo.environment}</Typography>
            )}
            {buildInfo.message && (
              <Typography variant="caption" sx={{ display: 'block' }}>Description: {buildInfo.message}</Typography>
            )}
            <Box sx={{ mt: 0.5 }}>
              {buildInfo.runUrl && (
                <Typography component="a" href={buildInfo.runUrl} target="_blank" rel="noreferrer" variant="caption" sx={{ mr: 1 }}>
                  View Run
                </Typography>
              )}
              {buildInfo.commitUrl && (
                <Typography component="a" href={buildInfo.commitUrl} target="_blank" rel="noreferrer" variant="caption">
                  View Commit
                </Typography>
              )}
            </Box>
          </Box>
        }
      >
        <Box
          sx={{
            position: 'absolute',
            bottom: 2,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'transparent',
            color: 'common.white',
            px: 0.75,
            py: 0.25,
            borderRadius: '4px',
            fontSize: { xs: '10px', sm: '11px', md: '12px' },
            lineHeight: 1,
            textShadow: '0 1px 2px rgba(0,0,0,0.6)',
            cursor: 'default',
            userSelect: 'none',
          }}
        >
          version {buildInfo.version ? `v${buildInfo.version} • ` : ''}#{buildInfo.runNumber}
        </Box>
      </Tooltip>
    );
  };

  return (
    <>
      <AppBar position="static" color="primary" elevation={1}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Logo and Title - Desktop */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mr: 1 }}>
              <Box sx={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}>
                <a href={`${window.location.protocol}//${window.location.host}`} style={{ display: 'flex', alignItems: 'center' }}>
                  <img 
                    src="/location-station-banner.svg" 
                    alt="Location Station" 
                    style={{ height: '64px', marginRight: '8px', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                  />
                </a>
                <VersionOverlay />
              </Box>
            </Box>

            {/* Mobile menu */}
            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' },
                }}
              >
                {pages.map((page) => {
                  const active = isActivePage(page.path);
                  return (
                    <MenuItem 
                      key={page.path} 
                      onClick={() => {
                        navigate(page.path);
                        handleCloseNavMenu();
                      }}
                      selected={active}
                      sx={{
                        backgroundColor: active ? 'action.selected' : 'transparent',
                        '&:hover': {
                          backgroundColor: active ? 'action.selected' : 'action.hover',
                        },
                      }}
                    >
                      {page.icon}
                      <Typography textAlign="center" fontWeight={active ? 500 : 400}>
                        {page.title}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Menu>
            </Box>

            {/* Logo and Title - Mobile */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', mr: 1 }}>
              <Box sx={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}>
                <a href={`${window.location.protocol}//${window.location.host}`} style={{ display: 'flex', alignItems: 'center' }}>
                  <img 
                    src="/location-station-banner.svg" 
                    alt="Location Station" 
                    style={{ height: '28px', marginRight: '4px' }}
                  />
                </a>
                <VersionOverlay />
              </Box>
            </Box>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                flexGrow: 1,
                display: { xs: 'flex', md: 'none' },
                fontFamily: 'Lexend',
                fontWeight: 500,
              }}
            >
              LS Publisher
            </Typography>

            {/* Desktop menu */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, ml: 3 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  display: 'flex',
                  backgroundColor: 'action.hover',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}
              >
                {pages.map((page) => {
                  const active = isActivePage(page.path);
                  return (
                    <Button
                      key={page.path}
                      onClick={() => {
                        navigate(page.path);
                        handleCloseNavMenu();
                      }}
                      sx={{ 
                        my: 0, 
                        py: 1.5,
                        px: 3,
                        color: 'primary.contrastText', 
                        display: 'flex', 
                        alignItems: 'center',
                        backgroundColor: active ? 'action.selected' : 'transparent',
                        borderRadius: 0,
                        fontWeight: active ? 500 : 400,
                        position: 'relative',
                        '&:hover': {
                          backgroundColor: active ? 'action.selected' : 'action.hover',
                        },
                        '&::after': active ? {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: '15%',
                          width: '70%',
                          height: '3px',
                          backgroundColor: 'primary.contrastText',
                          borderRadius: '3px 3px 0 0',
                        } : {}
                      }}
                      startIcon={page.icon}
                    >
                      {page.title}
                    </Button>
                  );
                })}
              </Paper>
            </Box>

            {/* Theme toggle and User menu */}
            {user && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                  <IconButton color="inherit" onClick={toggleMode} aria-label="toggle color mode">
                    {mode === 'dark' ? <LightMode /> : <DarkMode />}
                  </IconButton>
                </Tooltip>
                <Box sx={{ flexGrow: 0 }}>
                  <Tooltip title="Open settings">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                      <Avatar alt={user.name} src={user.picture} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    sx={{ mt: '45px' }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                  >
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        Signed in as {user.email}
                      </Typography>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleClearCache} disabled={clearCacheBusy}>
                      <CleaningServices fontSize="small" sx={{ mr: 1 }} />
                      <Typography textAlign="center">Clear cache</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => {
                      navigate('/profile');
                      handleCloseUserMenu();
                    }}>
                      <Settings fontSize="small" sx={{ mr: 1 }} />
                      <Typography textAlign="center">Profile</Typography>
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                      <Logout fontSize="small" sx={{ mr: 1 }} />
                      <Typography textAlign="center">Logout</Typography>
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Dialog open={clearCacheBusy} aria-labelledby="clear-cache-progress" maxWidth="xs" fullWidth>
        <DialogTitle id="clear-cache-progress">Clearing cache</DialogTitle>
        <DialogContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Please wait.
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog
        open={clearCacheResultOpen}
        onClose={() => setClearCacheResultOpen(false)}
        aria-labelledby="clear-cache-result"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="clear-cache-result">Clear cache</DialogTitle>
        <DialogContent>
          {clearCacheResult?.success ? (
            <Alert severity="success">{clearCacheResult?.message}</Alert>
          ) : (
            <Alert severity="error">{clearCacheResult?.message || 'Failed to clear cache.'}</Alert>
          )}

          {clearCacheResult?.errors && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Details
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  overflow: 'auto',
                  maxHeight: 300,
                  fontSize: 12,
                }}
              >
                {JSON.stringify(clearCacheResult.errors, null, 2)}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearCacheResultOpen(false)} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppHeader;